import { supabase } from "@/integrations/supabase/client";
import type {
  EventResult,
  EventResultsData,
  TeamMemberInfo,
  AnswerGroup,
} from "@/types/results";
import type { Database } from "@/integrations/supabase/types";
import { parseTimeToSeconds, formatSeconds } from "@/lib/utils";
import { personResultGender } from "@/lib/gender";
import { thinkSegmentRanges } from "@/lib/segments";
import { calculateResultTiming, resolveGender } from "@/lib/resultUtils";
import { logger, serializeError } from "@/lib/logger";
import { isVirtualLocationId, VIRTUAL_LOCATION } from "@/lib/virtual";
import type { RunSegmentConfig } from "@shared/gpxTrack";

type PersonRow = Database["public"]["Tables"]["person"]["Row"];
type ResultRow = Pick<
  Database["public"]["Views"]["v_result"]["Row"],
  "total_time" | "total_answers"
> & {
  times: (string | null)[] | null;
  run_times: (string | null)[] | null;
  think_times: (string | null)[] | null;
  answer_is_correct: (boolean | null)[] | null;
};

/** Flat display fields the results table needs, sourced straight from the
 *  get_event_registrations RPC (no v_person/v_team/v_location round-trips). */
interface ParticipationDisplay {
  participationId: number;
  personId: number;
  firstName: string;
  lastName: string;
  gender: PersonRow["gender"];
  teamId: number | null;
  teamName: string | null;
  /** Null: an unanchored virtual participation. */
  locationId: number | null;
  eventId: number | null;
  city: string;
  country: string | null;
  format: Database["public"]["Enums"]["participation_format"] | null;
  isThinker: boolean;
  result: ResultRow | null;
}

// Constants
export const ANSWER_BONUS_SECONDS = 150; // 2:30 bonus per correct answer

/**
 * Format time string without leading zero hours.
 * "0:13:38" => "13:38", "1:13:38" => "1:13:38"
 */
export const formatTimeWithoutZeroHours = (time: string | null): string => {
  if (!time) return "—";
  const negative = time.startsWith("-");
  const bare = negative ? time.slice(1) : time;
  const parts = bare.split(":");
  const prefix = negative ? "-" : "";
  if (parts.length === 3 && Number(parts[0]) === 0) {
    return `${prefix}${parts[1]}:${parts[2]}`;
  }
  return time;
};

/** Compute score: totalTime - bonusSeconds * correctAnswers */
export const computeScore = (
  time: string | null,
  correctCount: number | null,
  bonusSeconds: number = ANSWER_BONUS_SECONDS,
): string | null => {
  if (!time || correctCount === null) return null;
  const seconds = parseTimeToSeconds(time) - correctCount * bonusSeconds;
  return formatSeconds(seconds);
};

/**
 * Transform database participation record to EventResult format.
 * All derived values (totalTime, totalScore, answers counts) are computed dynamically.
 */
// Exported for timing regression tests. Not part of the
// public API surface — callers use fetchEventResults*.
export const transformParticipationToResult = (
  p: ParticipationDisplay,
  teamMembers: TeamMemberInfo[] | undefined,
  bonusSeconds: number = ANSWER_BONUS_SECONDS,
  segments: RunSegmentConfig[] = [],
  // Result rows created before the question-count fix may only have ten slots.
  // Keep the display arrays aligned with the question bank, not those stale rows.
  questionCount: number = 10,
): EventResult => {
  const { result } = p;
  const isTeam = p.teamId !== null;

  const category = isTeam ? "Team" : "Solo";
  const participantName = isTeam
    ? (p.teamName ?? "")
    : `${p.firstName} ${p.lastName}`.trim();
  const teamId = isTeam ? (p.teamId ?? undefined) : undefined;
  const format: "Onsite" | "Virtual" = p.format
    ? p.format === "virtual"
      ? "Virtual"
      : "Onsite"
    : isVirtualLocationId(p.locationId)
      ? "Virtual"
      : "Onsite";

  // Use shared gender resolution logic
  const gender = resolveGender(isTeam, teamMembers, p.gender);

  const shared = {
    participationId: p.participationId,
    locationId: p.locationId,
    eventId: p.eventId,
    personId: p.personId,
    category,
    participant: participantName,
    teamId,
    teamMembers,
    gender,
    city: p.city,
    country: p.country || "",
    format,
  };

  if (!result) {
    return {
      ...shared,
      ...calculateResultTiming({}, segments),
      hasResult: false,
      isDNF: false,
      answerGroups: [],
      totalAnswers: null,
      totalScore: null,
      questionAnswers: Array(questionCount).fill(0),
      questionReview: Array(questionCount).fill(null),
    };
  }

  const timing = calculateResultTiming(result, segments);

  // v_result types answer_is_correct as boolean[], but the DB column allows
  // per-element nulls (unreviewed). Read through a nullable view for review.
  const rawCorrect = (result.answer_is_correct ?? []).slice(0, questionCount);
  const correctAnswers = rawCorrect;
  // Detailed per-part data exists when the review array has any non-null entry.
  // Use presence (not truthiness) so an all-wrong player (every entry false)
  // still gets a real 0 · 0 split instead of "? · ?".
  const hasDetailedAnswerData = rawCorrect.some(
    (v) => v !== null && v !== undefined,
  );

  let totalAnswers: number | null = null;

  if (hasDetailedAnswerData) {
    totalAnswers = correctAnswers.filter(Boolean).length;
  } else if (
    result.total_answers !== null &&
    result.total_answers !== undefined
  ) {
    // Bronnitsy-style: only total_answers available, per-part unknown
    totalAnswers = result.total_answers;
  }

  const groupLengths =
    segments.length > 0
      ? segments
          .filter((segment) => segment.type === "think")
          .map((segment) => segment.length)
      : questionCount === 10
        ? [5, 5]
        : [questionCount];
  const answerGroups: AnswerGroup[] = [];
  for (const { lo, hi } of thinkSegmentRanges(
    groupLengths.length > 0 ? groupLengths : [questionCount],
    questionCount,
  )) {
    const to = Math.min(hi, questionCount);
    if (to >= lo) {
      answerGroups.push({
        from: lo,
        to,
        correct: hasDetailedAnswerData
          ? correctAnswers.slice(lo - 1, to).filter(Boolean).length
          : null,
      });
    }
  }

  const totalScore = computeScore(timing.totalTime, totalAnswers, bonusSeconds);

  const questionAnswers = correctAnswers.map((isCorrect) =>
    isCorrect ? 1 : 0,
  );
  while (questionAnswers.length < questionCount) {
    questionAnswers.push(0);
  }

  // Nulls preserved: null = not yet reviewed (amber ? in the matrix).
  const questionReview: (boolean | null)[] = rawCorrect.map((v) => v ?? null);
  while (questionReview.length < questionCount) {
    questionReview.push(null);
  }

  return {
    ...shared,
    ...timing,
    hasResult: true,
    answerGroups,
    totalAnswers,
    totalScore,
    questionAnswers,
    questionReview,
  };
};

/**
 * Fetches event results from database for a specific quizrun
 * Uses public views (v_*) for anonymous access.
 * Accepts optional prefetched quizrun data to avoid duplicate queries.
 */
export const fetchEventResultsFromDatabase = async (
  quizrunId: number,
  prefetchedQuizrun?: {
    date_start: string;
    number: number | null;
    language: string;
    title?: string | null;
  } | null,
): Promise<EventResultsData> => {
  // Use prefetched quizrun if provided (undefined = not provided, null = not found)
  let quizrun = prefetchedQuizrun;
  if (prefetchedQuizrun === undefined) {
    const { data, error: quizrunError } = await supabase
      .from("v_quizrun")
      .select("*")
      .eq("id", quizrunId)
      .maybeSingle();

    if (quizrunError) {
      throw quizrunError;
    }
    quizrun = data;
  }

  // Read bonus from v_quizrun, not the base table: participants are RLS-gated off
  // quizrun and would silently get null → default 150s → wrong scores on Box Hill (240).
  let bonusSeconds = ANSWER_BONUS_SECONDS;
  {
    const { data: bonusRow, error: bonusError } = await supabase
      .from("v_quizrun")
      .select("answer_bonus_seconds")
      .eq("id", quizrunId)
      .maybeSingle();
    // Non-fatal: fall back to the default bonus rather than failing the page.
    if (bonusError) {
      logger.error("Error fetching answer_bonus_seconds", serializeError(bonusError));
    }
    if (bonusRow?.answer_bonus_seconds != null) {
      bonusSeconds = bonusRow.answer_bonus_seconds;
    }
  }

  let questionCount = 10;
  {
    const { data, error } = await supabase.rpc(
      "quizrun_question_count" as never,
      { p_quizrun_id: quizrunId } as never,
    );
    if (error) {
      if (error.code !== "PGRST202" && error.code !== "42883") {
        throw error;
      }
    } else if (typeof data === "number") {
      questionCount = data;
    }
  }

  if (!quizrun) {
    return {
      eventId: String(quizrunId),
      eventName: `Quizrun #${quizrunId}`,
      eventDate: new Date().toISOString(),
      results: [],
      questionCount,
    };
  }

  // Fetch events for this quizrun from public view
  const { data: events, error: eventsError } = await supabase
    .from("v_event")
    .select("id")
    .eq("quizrun_id", quizrunId);

  if (eventsError) {
    throw eventsError;
  }

  const eventIds = (events || []).map((e) => e.id);

  if (eventIds.length === 0) {
    return {
      eventId: String(quizrunId),
      eventName: `Quizrun #${quizrunId}`,
      eventDate: quizrun.date_start,
      results: [],
      questionCount,
    };
  }

  let segments: RunSegmentConfig[] = [];
  {
    const { data, error } = await supabase.rpc("quizrun_result_segments", {
      p_quizrun_id: quizrunId,
    });
    if (error) {
      throw error;
    }
    segments = (data ?? []).map((segment) => {
      if (
        (segment.type !== "run" && segment.type !== "think") ||
        !Number.isFinite(segment.length) ||
        segment.length < 0 ||
        (segment.type === "think" && !Number.isInteger(segment.length))
      ) {
        throw new Error("Invalid quizrun result segment configuration.");
      }
      return {
        type: segment.type,
        length: segment.length,
        background: segment.background,
      };
    });
  }

  // Registration list comes from the get_event_registrations RPC for EVERY
  // role. The view path (v_participation / base participation+participation_member)
  // runs security_invoker, so under RLS a non-admin host only sees their own
  // membership row and the !inner join starves — anon (via this same RPC) would
  // then see MORE participants than a signed-in host. The RPC is SECURITY
  // DEFINER and returns only the public registration fields (no scoring, no
  // contact data), so it's the correct role-agnostic source for the list.
  // Scoring is layered on later via v_result (its own authorized path).
  // See 20260620_event_registrations_public_rpc.sql.
  //
  // The RPC already returns names, team, city and country, so we map straight
  // from it — no v_person / v_team / v_location round-trips, and the list no
  // longer depends on those views being readable for the viewer's role.
  let registrations: ParticipationDisplay[] = [];

  {
    const { data, error } = await supabase.rpc("get_event_registrations", {
      p_quizrun_id: quizrunId,
    });
    if (error) {
      // Only swallow the "RPC not deployed yet" case (migration not applied) so
      // the page degrades to its empty state instead of a white-screen. Any
      // other error (SQL, permissions, network) must surface, not hide.
      // PGRST202 = PostgREST can't find the function; 42883 = Postgres "function
      // does not exist" (belt and braces).
      if (error.code === "PGRST202" || error.code === "42883") {
        logger.error(
          "get_event_registrations missing (migration not applied?)",
          serializeError(error),
        );
        return {
          eventId: String(quizrunId),
          eventName: quizrun.title,
          eventDate: quizrun.date_start,
          results: [],
          questionCount,
        };
      }
      throw error;
    }
    registrations = (data ?? []).map((r) => ({
      participationId: r.participation_id,
      personId: r.person_id,
      firstName: r.first_name ?? "",
      lastName: r.last_name ?? "",
      gender: r.gender,
      teamId: r.team_id,
      teamName: r.team_name,
      locationId: r.location_id,
      eventId: r.event_id ?? null,
      city:
        r.city ??
        (isVirtualLocationId(r.location_id) ? VIRTUAL_LOCATION.city : ""),
      country: r.country,
      format: r.format,
      isThinker: r.is_thinker ?? false,
      result: null,
    }));
  }

  if (registrations.length === 0) {
    return {
      eventId: String(quizrunId),
      eventName: quizrun.title,
      eventDate: quizrun.date_start,
      results: [],
      questionCount,
    };
  }

  // Only scores still need a separate fetch (the RPC doesn't carry them).
  const participationIds = registrations.map((r) => r.participationId);
  const { data: resultRows, error: resultsError } = await supabase
    .from("v_result")
    .select("*")
    .in("participation_id", participationIds);
  if (resultsError) throw resultsError;

  const resultMap = new Map(
    (resultRows || []).map((r) => [r.participation_id, r]),
  );

  // Team members per team, built from the RPC rows (one row per member).
  const teamMembersMap = new Map<number, TeamMemberInfo[]>();
  for (const r of registrations) {
    if (r.teamId === null) continue;
    if (!teamMembersMap.has(r.teamId)) teamMembersMap.set(r.teamId, []);
    teamMembersMap.get(r.teamId)!.push({
      id: r.personId,
      name: `${r.firstName} ${r.lastName}`.trim(),
      gender: personResultGender(r.gender),
      isThinker: r.isThinker,
    });
  }

  // One row per team (the RPC returns a row per member); solos pass through.
  const processedTeams = new Set<number>();
  const results: EventResult[] = [];
  for (const r of registrations) {
    if (r.teamId !== null) {
      if (processedTeams.has(r.teamId)) continue;
      processedTeams.add(r.teamId);
    }
    const teamMembers =
      r.teamId !== null ? teamMembersMap.get(r.teamId) : undefined;
    results.push(
      transformParticipationToResult(
        { ...r, result: resultMap.get(r.participationId) ?? null },
        teamMembers,
        bonusSeconds,
        segments,
        questionCount,
      ),
    );
  }

  const eventDate = quizrun.date_start;
  const eventName = quizrun.title;

  return {
    eventId: String(quizrunId),
    eventName,
    eventDate,
    results,
    questionCount,
    answerBonusSeconds: bonusSeconds,
  };
};

/**
 * Fetches event results - always from database.
 * Both finished and in-progress events use the same DB source. The registration
 * list (including registered-but-unscored participants) comes from the
 * get_event_registrations RPC for all roles.
 */
export const fetchEventResults = async (
  quizrunId: number,
): Promise<EventResultsData> => {
  return fetchEventResultsFromDatabase(quizrunId, undefined);
};
